import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function CaseStudiesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeFilter, setActiveFilter] = useState('すべて');

  useSEO({
    title: '施工事例 | 岡山市の屋根工事実績2,000棟以上 | おかやま屋根工事',
    description: '岡山市で2,000棟以上の施工実績から厳選した屋根工事の事例をご紹介。雨漏り修理、屋根葺き替え、屋根塗装、防水工事のビフォーアフター写真とお客様の声を掲載。確かな技術と丁寧な施工をご確認ください。',
    keywords: '岡山市,屋根工事,施工事例,実績,雨漏り修理,屋根葺き替え,屋根塗装,ビフォーアフター',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema('施工事例', '岡山市で2,000棟以上の施工実績から厳選した屋根工事の事例をご紹介。'),
        generateBreadcrumbSchema([
          { name: 'ホーム', url: '/' },
          { name: '施工事例', url: '/case-studies' }
        ])
      ]
    }
  });

  const filters = ['すべて', '雨漏り修理', '葺き替え', '塗装', '防水工事'];

  const caseStudies = [
    {
      id: 1,
      title: '岡山市北区 K様邸',
      category: '葺き替え',
      location: '岡山市北区',
      duration: '10日間',
      cost: '約150万円',
      description: '築30年の瓦屋根を新しい軽量瓦に葺き替え。耐震性も向上し、お客様に大変ご満足いただきました。',
      beforeImage: 'https://public.readdy.ai/ai/img_res/b190730d222e0878d581851c286bf75d.jpg',
      afterImage: 'https://readdy.ai/api/search-image?query=Beautiful%20new%20traditional%20Japanese%20tile%20roof%20after%20complete%20renovation%20in%20Okayama%2C%20clean%20and%20professional%20finish%20without%20any%20text%20or%20signage%2C%20showing%20quality%20craftsmanship&width=800&height=600&seq=case102&orientation=landscape'
    },
    {
      id: 2,
      title: '岡山市中区 T様邸',
      category: '雨漏り修理',
      location: '岡山市中区',
      duration: '2日間',
      cost: '約8万円',
      description: '天井からの雨漏りを徹底調査。原因となっていた谷樋の劣化を修理し、完全に雨漏りを解決しました。',
      beforeImage: 'https://readdy.ai/api/search-image?query=Damaged%20roof%20valley%20gutter%20with%20water%20leak%20problem%20before%20repair%2C%20close-up%20detail%20photography%20without%20any%20text%20or%20signage%2C%20showing%20deterioration&width=800&height=600&seq=case201&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=Repaired%20roof%20valley%20gutter%20with%20new%20waterproofing%20after%20professional%20repair%20work%2C%20clean%20finish%20without%20any%20text%20or%20signage%2C%20showing%20quality%20repair&width=800&height=600&seq=case202&orientation=landscape'
    },
    {
      id: 3,
      title: '岡山市南区 S様邸',
      category: '塗装',
      location: '岡山市南区',
      duration: '5日間',
      cost: '約70万円',
      description: 'スレート屋根の塗装工事。遮熱塗料を使用し、夏の暑さ対策も実現。美観も大幅に向上しました。',
      beforeImage: 'https://readdy.ai/api/search-image?query=Faded%20and%20weathered%20slate%20roof%20before%20painting%20showing%20discoloration%2C%20architectural%20photography%20without%20any%20text%20or%20signage%2C%20showing%20aging&width=800&height=600&seq=case301&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=Freshly%20painted%20slate%20roof%20with%20beautiful%20dark%20finish%20after%20professional%20coating%2C%20clean%20and%20uniform%20appearance%20without%20any%20text%20or%20signage&width=800&height=600&seq=case302&orientation=landscape'
    },
    {
      id: 4,
      title: '岡山市東区 Y様邸',
      category: '防水工事',
      location: '岡山市東区',
      duration: '3日間',
      cost: '約45万円',
      description: 'ベランダの防水工事。ひび割れから雨水が浸入していた問題を解決し、10年保証で安心をお届けしました。',
      beforeImage: 'https://readdy.ai/api/search-image?query=Cracked%20balcony%20floor%20with%20waterproofing%20damage%20before%20repair%2C%20showing%20deterioration%20and%20water%20damage%20without%20any%20text%20or%20signage&width=800&height=600&seq=case401&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=Newly%20waterproofed%20balcony%20floor%20with%20smooth%20professional%20finish%20after%20repair%2C%20clean%20surface%20without%20any%20text%20or%20signage&width=800&height=600&seq=case402&orientation=landscape'
    },
    {
      id: 5,
      title: '岡山市北区 M様邸',
      category: '葺き替え',
      location: '岡山市北区',
      duration: '8日間',
      cost: '約130万円',
      description: '老朽化したスレート屋根を金属屋根に葺き替え。軽量化により建物への負担も軽減されました。',
      beforeImage: 'https://readdy.ai/api/search-image?query=Old%20deteriorated%20slate%20roof%20before%20replacement%20showing%20severe%20aging%20and%20damage%2C%20architectural%20photography%20without%20any%20text%20or%20signage&width=800&height=600&seq=case501&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=New%20modern%20metal%20roof%20after%20complete%20replacement%20in%20Okayama%2C%20sleek%20professional%20finish%20without%20any%20text%20or%20signage%2C%20showing%20modern%20quality&width=800&height=600&seq=case502&orientation=landscape'
    },
    {
      id: 6,
      title: '岡山市南区 H様邸',
      category: '雨漏り修理',
      location: '岡山市南区',
      duration: '1日間',
      cost: '約5万円',
      description: '台風後の雨漏り緊急対応。瓦のズレを修正し、即日で雨漏りを止めることができました。',
      beforeImage: 'https://readdy.ai/api/search-image?query=Displaced%20roof%20tiles%20after%20typhoon%20damage%20showing%20gaps%20and%20misalignment%2C%20emergency%20repair%20situation%20without%20any%20text%20or%20signage&width=800&height=600&seq=case601&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=Properly%20aligned%20and%20secured%20roof%20tiles%20after%20emergency%20repair%2C%20neat%20professional%20finish%20without%20any%20text%20or%20signage&width=800&height=600&seq=case602&orientation=landscape'
    }
  ];

  const filteredCases = activeFilter === 'すべて'
    ? caseStudies
    : caseStudies.filter(c => c.category === activeFilter);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/82d7c42079d55ce6400b720be1705683.jpeg"
            alt="岡山市 施工事例"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">施工事例</h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
            2,000棟以上の実績から厳選した施工事例をご紹介
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 sm:py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base ${
                  activeFilter === filter
                    ? 'bg-[#1e3a5f] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {filteredCases.map((caseStudy) => (
              <CaseStudyCard key={caseStudy.id} caseStudy={caseStudy} />
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">お客様の声</h2>
            <p className="text-base sm:text-lg text-gray-600">施工後のお客様から嬉しいお言葉をいただいています</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <ReviewCard
              name="田中様"
              location="岡山市中区"
              image="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20middle-aged%20Japanese%20man%20homeowner%20smiling%20confidently%2C%20clean%20simple%20background%20without%20any%20text%2C%20natural%20lighting%2C%20headshot%20style&width=200&height=200&seq=review101&orientation=squarish"
              review="雨漏りで困っていましたが、原因をしっかり調査して根本から解決していただきました。職人さんの説明も丁寧で、安心してお任せできました。工事中も毎日進捗を報告してくださり、安心して任せられました。仕上がりも美しく、10年保証も付いているので長く安心して暮らせます。"
            />
            <ReviewCard
              name="佐藤様"
              location="岡山市北区"
              image="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/abe852ef421be38fa3dce1ea65a3c7bc.jpeg"
              review="見積もりが明確で、追加費用も一切ありませんでした。工事中も毎日進捗を報告してくださり、安心して任せられました。仕上がりも美しく、10年保証も付いているので長く安心して暮らせます。"
            />
            <ReviewCard
              name="山田様"
              location="岡山市南区"
              image="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elderly%20Japanese%20man%20homeowner%20smiling%20kindly%2C%20clean%20simple%20background%20without%20any%20text%2C%20natural%20lighting%2C%20headshot%20style&width=200&height=200&seq=review103&orientation=squarish"
              review="地元の会社で安心感がありました。工事中も近隣への配慮が行き届いており、プロフェッショナルな対応に感謝しています。屋根が新しくなり、家全体が明るくなった気がします。"
            />
            <ReviewCard
              name="鈴木様"
              location="岡山市東区"
              image="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20young%20Japanese%20woman%20homeowner%20smiling%20happily%2C%20clean%20simple%20background%20without%20any%20text%2C%20natural%20lighting%2C%20headshot%20style&width=200&height=200&seq=review104&orientation=squarish"
              review="初めての屋根工事で不安でしたが、丁寧な説明と確かな技術で安心してお願いできました。遮熱塗料のおかげで夏の暑さも軽減され、快適に過ごせています。本当にありがとうございました。"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 sm:py-24 lg:py-32">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20Okayama%20city%20skyline%20with%20clear%20blue%20sky%20and%20traditional%20Japanese%20architecture%2C%20peaceful%20urban%20landscape%20without%20any%20text%20or%20signage%2C%20professional%20cityscape%20photography&width=1920&height=800&seq=casecta001&orientation=landscape"
            alt="岡山市 街並み"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
            無料見積もり・ご相談<br />
            今すぐお電話ください
          </h2>
          <p className="text-lg sm:text-xl text-white mb-8 sm:mb-12">
            営業時間 9:00〜18:00
          </p>
          
          <a
            href="tel:0860123456"
            className="inline-flex items-center justify-center gap-4 px-8 sm:px-12 py-5 sm:py-6 bg-[#ff6b35] rounded-2xl hover:bg-[#e55a28] transition-all duration-300 shadow-2xl cursor-pointer mb-6"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-phone-fill text-2xl sm:text-3xl text-white"></i>
            </div>
            <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white whitespace-nowrap">086-012-3456</span>
            <i className="ri-arrow-right-up-line text-2xl sm:text-3xl text-white"></i>
          </a>
          
          <p className="text-sm sm:text-base text-white/80">または</p>
          
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 mt-4 text-base sm:text-lg font-bold text-[#1e3a5f] bg-white rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
          >
            お問い合わせフォームへ
            <i className="ri-mail-line ml-2 text-xl"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}

function CaseStudyCard({ caseStudy }: { caseStudy: any }) {
  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
      <div className="grid grid-cols-2 gap-2 p-4">
        <div className="relative">
          <img
            src={caseStudy.beforeImage}
            alt={`${caseStudy.title} 施工前`}
            className="w-full h-48 object-cover object-top rounded-lg"
          />
          <span className="absolute top-2 left-2 px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
            施工前
          </span>
        </div>
        <div className="relative">
          <img
            src={caseStudy.afterImage}
            alt={`${caseStudy.title} 施工後`}
            className="w-full h-48 object-cover object-top rounded-lg"
          />
          <span className="absolute top-2 left-2 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
            施工後
          </span>
        </div>
      </div>
      
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-3 py-1 bg-[#1e3a5f] text-white text-xs font-semibold rounded-full">
            {caseStudy.category}
          </span>
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{caseStudy.title}</h3>
        
        <div className="grid grid-cols-3 gap-4 mb-4 pb-4 border-b border-gray-200">
          <div>
            <p className="text-xs text-gray-500 mb-1">所在地</p>
            <p className="text-sm font-semibold text-gray-900">{caseStudy.location}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">工期</p>
            <p className="text-sm font-semibold text-gray-900">{caseStudy.duration}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">費用</p>
            <p className="text-sm font-semibold text-gray-900">{caseStudy.cost}</p>
          </div>
        </div>
        
        <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{caseStudy.description}</p>
      </div>
    </div>
  );
}

function ReviewCard({ name, location, image, review }: { name: string; location: string; image: string; review: string }) {
  return (
    <div className="bg-gray-50 rounded-xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16">
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover object-top rounded-full"
          />
        </div>
        <div>
          <h4 className="font-bold text-gray-900 text-base sm:text-lg">{name}</h4>
          <p className="text-sm text-gray-600">{location}</p>
        </div>
      </div>
      <i className="ri-double-quotes-l text-3xl text-[#1e3a5f]/20 mb-4 block"></i>
      <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{review}</p>
    </div>
  );
}
